/**
 * ONE in-app PDF preview, wherever a stored document is opened
 * (`docs/specs/strategic-architecture/governed-email-content-retrieval-spec.md` §3.1, §9).
 *
 * PRESENTATIONAL ONLY: it is handed a Blob its parent already fetched through the governed,
 * audited byte door. It never fetches, and it never decides who may read a file.
 *
 * ⛔ **The object URL is memory-only and is revoked on close, on unmount, and whenever the Blob it
 * was minted from changes** (spec §3.3). This is not tidiness — an un-revoked `blob:` URL is a
 * readable handle on the bytes that outlives the authorization that produced them, and it survives
 * a principal change, a navigation and a sign-out. The revoke lives in the effect's cleanup so
 * every one of those paths runs it, including the ones nobody remembered to write a handler for.
 *
 * ⛔ **No bearer token ever rides in a URL** and no SAS or external viewer URL is minted (spec §9).
 * The bytes reached the browser through the authenticated client; this shows what is already here.
 *
 * `<object>` rather than `<iframe>`: a browser that cannot render a PDF falls back to the element's
 * children instead of painting a blank frame, which is where the Download fallback lives — the
 * "provide a download fallback if browser PDF rendering is unavailable" requirement, rendered by
 * the browser itself rather than guessed at from a user-agent string.
 */
import { useEffect, useState } from 'react';

import styles from './PdfViewer.module.css';

export interface PdfViewerProps {
  /** The already-fetched bytes. `null` closes the viewer (and revokes the previous URL). */
  blob: Blob | null;
  /** Shown in the header and used as the download filename. */
  name?: string | null;
  onClose: () => void;
  className?: string;
}

export function PdfViewer({ blob, name, onClose, className }: PdfViewerProps) {
  const [url, setUrl] = useState<string | null>(null);

  // ⚠ `react-hooks/set-state-in-effect` warns on the `setUrl(next)` below and the warning is
  // accepted here, deliberately. Minting an object URL ALLOCATES in an external registry, so it
  // cannot move into render: StrictMode double-invokes render and would leak one URL per open —
  // precisely the leak this component exists to prevent. This is the case the rule's own text
  // carves out ("synchronize state between React and an external system"), and the revoke has to
  // live in the matching cleanup for the unmount path to work at all.
  useEffect(() => {
    // No `setUrl(null)` here: when `blob` goes null the PREVIOUS run's cleanup has already revoked
    // and cleared it. Clearing again would be a redundant synchronous setState — and the redundancy
    // is what hides which line is actually doing the work.
    if (!blob) return undefined;
    const next = URL.createObjectURL(blob);
    setUrl(next);
    // The cleanup runs on unmount, on close (blob → null) AND when a different file is opened in
    // the same viewer — three revocation paths, one line, none of them forgettable.
    return () => {
      URL.revokeObjectURL(next);
      setUrl(null);
    };
  }, [blob]);

  if (!blob || !url) return null;

  const label = name || 'document.pdf';
  return (
    <div className={className ? `${styles.wrap} ${className}` : styles.wrap} data-pdf-viewer="open">
      <div className={styles.head}>
        <span className={styles.name} title={label}>{label}</span>
        <span className={styles.headActions}>
          {/* Same object URL, so the fallback costs no second gated read — and it dies with the
              viewer, exactly like the preview. */}
          <a className={styles.action} href={url} download={label}>Download</a>
          <button type="button" className={styles.action} onClick={onClose}>Close</button>
        </span>
      </div>
      <object className={styles.frame} data={url} type="application/pdf" aria-label={label}>
        <div className={styles.fallback}>
          This browser cannot display PDFs inline.{' '}
          <a href={url} download={label}>Download {label}</a>
        </div>
      </object>
    </div>
  );
}

export default PdfViewer;
